import { AppContext } from "../core/context";
import { productLibrary, seriesLibrary } from "../data";
import { Entity, Module, Product } from "../types/type_def";
import { ConversionHelper } from "../utils/conversion";
import { MathHelper } from "../utils/math";
import { getSeriesDepth } from "./construction";
import { LegHelper } from "./leg-helper";

export function createAttachmentPoint(ctx: AppContext, id: string, tags: string, parent: BABYLON.TransformNode | BABYLON.AbstractMesh, position: BABYLON.Vector3, rotation: BABYLON.Vector3, scaling: BABYLON.Vector3): BABYLON.TransformNode {

    let attachmentNode = parent.getChildTransformNodes(true, x => x.id.replace(parent.id+"_attachment_","") === id)[0];
    if (!attachmentNode) {
        let attachmentNode = new BABYLON.TransformNode(parent.id + "_attachment_" + id, ctx.scene);

        attachmentNode.parent = parent;
        attachmentNode.rotation = rotation;
        attachmentNode.position = position;
        attachmentNode.scaling = scaling;

        BABYLON.Tags.EnableFor(attachmentNode);
        BABYLON.Tags.AddTagsTo(attachmentNode, tags);
    }

    return attachmentNode;
}

export function createAttachmentPointRotated(ctx: AppContext, id: string, tags: string, parent: BABYLON.TransformNode | BABYLON.AbstractMesh, position: BABYLON.Vector3, rotation: BABYLON.Vector3, scaling: BABYLON.Vector3, pivotNode?: BABYLON.TransformNode): BABYLON.TransformNode {

   // let attachmentNode = parent.getChildTransformNodes(true, x => x.id.includes(id))[0];
    //if (!attachmentNode) {
       let attachmentNode = new BABYLON.TransformNode(parent.id + "_attachment_" + id, ctx.scene);

        attachmentNode.parent = parent;      
        attachmentNode.scaling = scaling;      

        if (pivotNode) {           
        
            pivotNode.computeWorldMatrix(true);
            attachmentNode.position = new BABYLON.Vector3(pivotNode.position.x, 0, pivotNode.position.z);
            attachmentNode.position.addInPlace(position.subtract(new BABYLON.Vector3(pivotNode.position.x, 0, pivotNode.position.z)));
            attachmentNode.rotation.addInPlace(rotation);

            if (pivotNode.rotation) {
                attachmentNode.rotateAround(pivotNode.position, BABYLON.Vector3.Up(), pivotNode.rotation.y + rotation.y);
            }         


        } else {
            attachmentNode.position = position;
            attachmentNode.rotation = rotation;
        }

        BABYLON.Tags.EnableFor(attachmentNode);
        BABYLON.Tags.AddTagsTo(attachmentNode, tags);
   // }

    return attachmentNode;
}

export async function createFreestandingAttachments(ctx: AppContext, entity: Entity, entityNode: BABYLON.TransformNode, positionX: number, positionZ: number, offsetX: number, offsetZ: number) {

    const entityProduct = productLibrary.getProductByProductNo(entity.productNo);
    return seriesLibrary.fetchSeriesByID(entityProduct.series).then(seriesData => {

        const frontLeftLeg = seriesLibrary.getSeriesLeg(seriesData, "standard", "LT06")
        const frontRightLeg = seriesLibrary.getSeriesLeg(seriesData, "standard", "LT07")
        const backLeftLeg = seriesLibrary.getSeriesLeg(seriesData, "standard", "LT09")
        const backRightLeg = seriesLibrary.getSeriesLeg(seriesData, "standard", "LT10")

        let frontLeftLegType = (frontLeftLeg) ? frontLeftLeg.type.identifier.toLocaleLowerCase() : "lt01"
        let frontRightLegType = (frontRightLeg) ? frontRightLeg.type.identifier.toLocaleLowerCase() : "lt01"
        let backLeftLegType = (backLeftLeg) ? backLeftLeg.type.identifier.toLocaleLowerCase() : "lt01"
        let backRightLegType = (backRightLeg) ? backRightLeg.type.identifier.toLocaleLowerCase() : "lt01"

        createAttachmentPointRotated(ctx, "left_front", `leg ${frontLeftLegType}`, entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
        createAttachmentPointRotated(ctx, "left_back", `leg ${backLeftLegType}`, entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
        createAttachmentPointRotated(ctx, "right_back", `leg ${backRightLegType}`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
        createAttachmentPointRotated(ctx, "right_front", `leg ${frontRightLegType}`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
    })
}

export function removeLegAttachment(ctx: AppContext, attachment: string, entityNode: BABYLON.TransformNode) {
    const attachmentNodes = entityNode.getChildTransformNodes(true, x => x.id.includes(attachment))
    if (attachmentNodes) {
        for (let attachmentNode of attachmentNodes) {
            const tags = BABYLON.Tags.GetTags(attachmentNode)
            if (tags) {
                const legType = tags.split(" ")[1];
                if (["lt01","lt06","lt07","lt09","lt10"].includes(legType)) {
                    attachmentNode.dispose(false);
                }
            }
        }
    }
}

export async function createLegAttachments(ctx: AppContext, module: Module, entity: Entity, product: Product, scene: BABYLON.Scene) {

    //Freestyle
    if (![148882,157546,157547].includes(product.series)) {
       await createLegAttachmentsFreestyle(ctx, module, entity, product, scene);  
       // await createLegsFreeStyle(ctx, module, entity, product, scene);         
        modifyLegAttachments(ctx, module, entity, product, scene);     
    } else {
        createLegAttachmentsStandard(ctx, module, entity, product, scene);
    }

}

export async function createLegAttachmentsFreestyle(ctx: AppContext, module: Module, entity: Entity, product: Product, scene: BABYLON.Scene) {

    let entityConnectionType = ctx.moduleHelper.getEntityConnectionType(module, entity);
    var seriesDepth = ConversionHelper.scale(getSeriesDepth(ctx), ctx.scaleFactor) as number;

    let entityNode = scene.getTransformNodeByUniqueId(entity.uniqueId);
    if (entityNode) {

        let entityBounds = entityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        entityBounds.computeWorldMatrix(true);                
        let bb = entityBounds.getBoundingInfo().boundingBox//new BABYLON.BoundingBox(bv.min, bv.max);

        //let rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(-entity.rotation._y, entity.rotation._x, entity.rotation._z);
        //let entityExtendSize = BABYLON.Vector3.TransformCoordinates(bb.extendSize, rotationMatrix);

        /*var leftPivot = BABYLON.MeshBuilder.CreateBox("leftPivot", { size: 0.1 });
        leftPivot.isPickable = false;
        leftPivot.visibility = 0;
        leftPivot.parent = entityNode;
        leftPivot.position = new BABYLON.Vector3(bb.extendSize.x, bb.extendSize.y, bb.extendSize.z);

        var rightPivot = BABYLON.MeshBuilder.CreateBox("rightPivot", { size: 0.1 });
        rightPivot.isPickable = false;
        rightPivot.visibility = 0;
        rightPivot.parent = entityNode;
        rightPivot.position = new BABYLON.Vector3(-bb.extendSize.x, bb.extendSize.y, bb.extendSize.z);*/

        let productDepth = ConversionHelper.scale(product.depth, ctx.scaleFactor) as number;

        let positionX = Math.abs(bb.extendSize.x);
        let positionZ = Math.abs(bb.extendSize.z*2);
    
        await createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, 0,0);

        for (let connection of entity.connections) {

            var connectedEntity = module.entities.find(x => x.id === connection.entityId);
            if (!connectedEntity)
                continue;

            var connectedProduct = productLibrary.getProductByProductNo(connectedEntity.productNo);
            if (!connectedProduct)
                continue;
           
            var connectorNode = entityNode.getChildMeshes(true, x => x.name === connection.id)[0]
            if (!connectorNode) return;

            let pivotNode = null
            if (["CT10_L","CT05","CT06"].includes(connection.id))
                pivotNode = connectorNode;
          
            switch (connection.id) {
                case "CT03":
                case "CT01_L":
                {                  
                    if (productDepth <= seriesDepth) {
                        removeLegAttachment(ctx, "left_front", entityNode);
                    }
                  
                    removeLegAttachment(ctx, "left_back", entityNode);

                    if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                         !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                        continue;

                    createAttachmentPointRotated(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z - seriesDepth/2), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1), pivotNode);                                                           
                    createAttachmentPointRotated(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z + seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1), pivotNode);
                }
                    break;
                case "CT04":                   
                case "CT01_R":  
                {                             
                    if (productDepth <= seriesDepth) {
                        removeLegAttachment(ctx, "right_front", entityNode);
                    }

                    removeLegAttachment(ctx, "right_back", entityNode);

                    if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                         !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                        continue;

                    createAttachmentPointRotated(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z - seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1), pivotNode);
                    createAttachmentPointRotated(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z + seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1), pivotNode);
                }
                    break;
                case "CT10_L": {
                    removeLegAttachment(ctx, "left_front", entityNode); 
                    removeLegAttachment(ctx, "right_front", entityNode);   

                    if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                         !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                        continue;

                    createAttachmentPointRotated(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z - seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1), pivotNode);
                    createAttachmentPointRotated(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z + seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1), pivotNode);

                }
                    break;
                 case "CT10_R":                  
                {        
                    removeLegAttachment(ctx, "right_back", entityNode);
                    removeLegAttachment(ctx, "right_front", entityNode); 
                    
                    if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                         !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                        continue;

                    createAttachmentPointRotated(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z - seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1), pivotNode);
                    createAttachmentPointRotated(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z + seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1), pivotNode);
                }
                break;
                case "CT05": {
                    removeLegAttachment(ctx, "left_front", entityNode);

                     if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                         !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                        continue;
                    
                    createAttachmentPointRotated(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z + seriesDepth/2), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1), pivotNode);                                                           
                    createAttachmentPointRotated(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z - seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1), pivotNode);
                }
                    break;
                case "CT06": {
                    removeLegAttachment(ctx, "right_front", entityNode);
                    
                    if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                         !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                        continue;

                    createAttachmentPointRotated(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z + seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1), pivotNode);
                    createAttachmentPointRotated(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(connectorNode.position.x, 0, connectorNode.position.z - seriesDepth/2),  BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1), pivotNode);
                }
                    break;
                case "CT37":                                            
                {   
                    removeLegAttachment(ctx, "left_front", entityNode);
                    removeLegAttachment(ctx, "right_front", entityNode);

                    if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                        !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                        continue;  

                    createAttachmentPointRotated(ctx, "left_front", "leg lt02", entityNode,  new BABYLON.Vector3(connectorNode.position.x - seriesDepth/2, 0, connectorNode.position.z), new BABYLON.Vector3(0,-Math.PI/2,0), new BABYLON.Vector3(1, 1, 1), pivotNode);                                                           
                    createAttachmentPointRotated(ctx, "right_front", "leg lt02", entityNode,  new BABYLON.Vector3(connectorNode.position.x + seriesDepth/2, 0, connectorNode.position.z), new BABYLON.Vector3(0,-Math.PI/2,0), new BABYLON.Vector3(1, 1, -1), pivotNode);
                }
                    break;
                case "CT11":                    
                {
                    removeLegAttachment(ctx, "left_back", entityNode);
                    removeLegAttachment(ctx, "right_back", entityNode);

                    if (!(ctx.legConnections.filter(x => x.parentId === entity.id && x.childId === connection.entityId).length === 0) ||
                        !(ctx.legConnections.filter(x => x.parentId === connection.entityId && x.childId === entity.id).length === 0))
                            continue;
                 
                    createAttachmentPointRotated(ctx, "left_back", "leg lt02", entityNode,  new BABYLON.Vector3(connectorNode.position.x - seriesDepth/2, 0, connectorNode.position.z), new BABYLON.Vector3(0,-Math.PI/2,0), new BABYLON.Vector3(-1, 1, 1), pivotNode);
                    createAttachmentPointRotated(ctx, "right_back", "leg lt02", entityNode,  new BABYLON.Vector3(connectorNode.position.x + seriesDepth/2, 0, connectorNode.position.z), new BABYLON.Vector3(0,-Math.PI/2,0), new BABYLON.Vector3(-1, 1, -1), pivotNode);
                }

            }

            ctx.legConnections.push({ parentId: entity.id, childId: connection.entityId })

        }         
    }
}

export async function createLegAttachmentsStandard(ctx: AppContext, module: Module, entity: Entity, product: Product, scene: BABYLON.Scene) {

    var seriesDepth = ConversionHelper.scale(getSeriesDepth(ctx), ctx.scaleFactor) as number;
    var series = await seriesLibrary.fetchSeriesByID(product.series);       

    var entityConnectionType = ctx.moduleHelper.getEntityConnectionType(module, entity);
    const entityProduct = productLibrary.getProductByProductNo(entity.productNo);
    const adjacentEntities =  ctx.entityHelper.getAdjacentEntities(module, entity, scene);          

    var rotationSingle = 0;
    var offsetFrontSingle = 0;        
    var offsetSideSingle = 0;

    var legProductSingle = seriesLibrary.getSeriesLeg(series, "standard", "lt01");
    if (legProductSingle) {
        rotationSingle = legProductSingle.legRotation;
        offsetFrontSingle = ConversionHelper.scale(legProductSingle.legOffsetFromFront, ctx.scaleFactor) as number || 0;
        offsetSideSingle = ConversionHelper.scale(legProductSingle.legOffsetFromSide, ctx.scaleFactor) as number || 0;
    }

    var rotationConnection = 0;
    var offsetFrontConnection = 0;
    var offsetSideConnection = 0;
    
    var legProductConnection = seriesLibrary.getSeriesLeg(series, "standard", "lt02");
    if (legProductConnection) {
        rotationConnection = legProductConnection.legRotation;
        offsetFrontConnection = ConversionHelper.scale(legProductSingle.legOffsetFromFront, ctx.scaleFactor) as number || 0;
        offsetSideConnection = ConversionHelper.scale(legProductSingle.legOffsetFromSide, ctx.scaleFactor) as number || 0;
    }

    var entityNode = scene.getTransformNodeByUniqueId(entity.uniqueId);        
    if (entityNode) {           

        var entityBounds = entityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        entityBounds.refreshBoundingInfo()
        var bv = entityBounds.getHierarchyBoundingVectors(true);
        var bb = new BABYLON.BoundingBox(bv.min, bv.max);

        var offsetX = 0;
        var offsetZ = 0;

        let rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(-entity.rotation._y, entity.rotation._x, entity.rotation._z);            
        let entityExtendSize = BABYLON.Vector3.TransformCoordinates(bb.extendSize, rotationMatrix);    

        var positionX = Math.abs(entityExtendSize.x);
        var positionZ = Math.abs(entityExtendSize.z) * 2;

        let rightOffsetZ = positionZ;
        let rightDepth = 0;
        let entityRight = adjacentEntities.find(x => ["RIGHT", "RIGHT_BACK"].includes(x.direction));
        if (!entityRight)
            entityRight = adjacentEntities.find(x => ["RIGHT_FRONT"].includes(x.direction));

        let entityRightConnectionType = ""

        if (entityRight) {
            const entityProductRight = productLibrary.getProductByProductNo(entityRight.entity.productNo);
            const rightBb = ctx.entityHelper.getEntityBoundingBox(entityRight.entity, scene);
            let entityExtendSize = BABYLON.Vector3.TransformCoordinates(rightBb.extendSize, rotationMatrix);    
            rightDepth = entityProductRight.depth;
            rightOffsetZ = Math.abs(entityExtendSize.z) * 2;

            entityRightConnectionType = ctx.moduleHelper.getEntityConnectionType(module, entityRight.entity);
        }  
        
        let leftOffsetZ = positionZ;
        let leftDepth = 0;
        let entityLeft = adjacentEntities.find(x => ["LEFT","LEFT_BACK"].includes(x.direction));
        if (!entityLeft)
            entityLeft = adjacentEntities.find(x => ["LEFT_FRONT"].includes(x.direction));

        let entityLeftConnectionType = ""
     
        if (entityLeft) {
            const entityProductLeft = productLibrary.getProductByProductNo(entityLeft.entity.productNo);
            const leftBb = ctx.entityHelper.getEntityBoundingBox(entityLeft.entity, scene);
            let entityExtendSize = BABYLON.Vector3.TransformCoordinates(leftBb.extendSize, rotationMatrix);  
            leftDepth = entityProductLeft.depth;
            leftOffsetZ = Math.abs(entityExtendSize.z) * 2;

            entityLeftConnectionType = ctx.moduleHelper.getEntityConnectionType(module, entityLeft.entity);
        }   

        let entityFront =  adjacentEntities.find(x => ["FRONT"].includes(x.direction));
        let entityBack =  adjacentEntities.find(x => ["BACK"].includes(x.direction));

        //Has connections! 
        switch (entity.category) {

            case "CENTER_SECTION":
            case "CENTER_SECTION_LEFT_SHORTBACK":
            case "CENTER_SECTION_RIGHT_SHORTBACK":
            case "CENTER_SECTION_WO_BACK":
            case "COMBI_SECTION_LEFT_SHORTBACK":
            case "COMBI_SECTION_RIGHT_SHORTBACK":    {

                switch (entityConnectionType) {                      
                    case "CT02": {
                        let legFrontType = "lt02";

                        if (rightDepth > 0 && product.depth > rightDepth) {                                                   
                            createAttachmentPoint(ctx, "right_center", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, rightOffsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            legFrontType = "lt01"
                        }

                        if (leftDepth > 0 && product.depth > leftDepth) {
                            createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                        }

                        createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", `leg ${legFrontType}`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                    }
                        break;
                    case "CT03": {
                        if (leftDepth > 0 && product.depth > leftDepth) {
                            createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                        }

                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));  
                    }
                        break;
                    case "CT04": {
                        let legFrontType = "lt02";

                        if (rightDepth > 0 && product.depth > rightDepth) {                                                    
                            createAttachmentPoint(ctx, "right_center", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, rightOffsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            legFrontType = "lt01"
                        }

                        createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", `leg ${legFrontType}`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                    }
                        break;

                    case "CT37":
                    case "CT38":
                    case "CT39":   
                    case "CT40":    
                        createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ + 0.2), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ + 0.2), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                    break;    


                    default: 
                        createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                        break;
                }

            }
                break;
            case "CORNER_SECTION":
            case "CLOSING_CORNER_SECTION_LEFT":
            case "CLOSING_CORNER_SECTION_RIGHT": {

                let cornerLegType = series.legPackages.filter(x => x.type === "LT03").length > 0 ? "LT03" : "LT01"

                let frontOffsetZ = positionZ
                if (leftDepth > 0 && product.depth > leftDepth) {     
                    frontOffsetZ = leftOffsetZ  
                }
                if (rightDepth > 0 && product.depth > rightDepth) {
                    frontOffsetZ = rightOffsetZ   
                }

                switch (entityConnectionType) {
                    case "CT10":
                    case "CT10_L":
                    case "CT10_R": {
                        createAttachmentPoint(ctx, "left_back", `leg ${cornerLegType}`, entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, frontOffsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                    }
                        break;
                }

            }
                break;
            case "CLOSING_SECTION_LEFT":
            case "CLOSING_SECTION_LEFT_SHORTBACK": 
            case "CLOSING_SECTION_LEFT_WO_ARM": {

                switch (entityConnectionType) {                       
                    case "CT01": {
                        createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(Math.PI, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(0, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                    }
                        break;
                    case "CT05": {
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                    }
                        break;
                    case "CT08": {
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(Math.PI, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(0, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                    }
                        break;
                    default: 
                        createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                        break;
                }

            }
                break;
            case "CLOSING_SECTION_RIGHT":
            case "CLOSING_SECTION_RIGHT_SHORTBACK": 
            case "CLOSING_SECTION_RIGHT_WO_ARM": {
                switch (entityConnectionType) {
                    case "CT01": {
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                    }
                        break;
                    case "CT06": {
                        createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "middle_front", "leg lt02", entityNode, new BABYLON.Vector3((seriesDepth - positionX) - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                    }
                        break;
                    case "CT09": {
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3((seriesDepth - positionX) - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                    }
                        break;
                    default: 
                        createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                        break;
                }
            }
                break;
            case "CHAISELONG_LEFT":
            case "CHAISELONG_LEFT_WO_ARM":    
            case "CHAISELONG_RIGHT": 
            case "CHAISELONG_RIGHT_WO_ARM": {

                 switch (entityConnectionType) {       
                              
                    case "CT01": {

                        if (entity.connections.length === 0) {
                             createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ); 
                        }

                        if (entityLeft) {                               

                            createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                            createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());    
                        }
                        if (entityRight) {

                            let legFrontType = "lt02"

                            if (rightDepth > 0 && product.depth > rightDepth) {                                                    
                                createAttachmentPoint(ctx, "right_center", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, rightOffsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                                legFrontType = "lt01"
                            }

                            createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", `leg ${legFrontType}`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                            createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                        }
                     }
                    break;
                    default: 
                        createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                        break;
                 }
            }
            break;
            case "SOFA":
            case "SOFA_LEFT":
            case "SOFA_RIGHT": 
            case "SOFA_SECTIONAL": {

                switch (entityConnectionType) {                      
                    case "CT05": {
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                    }
                        break;
                    case "CT06": {
                        createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "middle_front", "leg lt02", entityNode, new BABYLON.Vector3((seriesDepth - positionX) - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                    }
                        break;
                    case "CT07": {
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "middle_front", "leg lt02", entityNode, new BABYLON.Vector3((seriesDepth - positionX) - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                    }
                        break;
                    default: 
                        createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                        break;
                }
            }
                break;
            case "FOOTSTOOL": {
                switch (entityConnectionType) {

                    case "CT00": {

                        const regex = /(Ø{1}(\({1}.*\){1}))|(Ø{1}\d.+)/g
                        const isRound = regex.test(product.name)

                        if (isRound) {                                           
                          
                            var bbx = entityNode.getHierarchyBoundingVectors(true);
                            var bb = new BABYLON.BoundingBox(bbx.min, bbx.max);
                            var radius = Math.abs(bb.extendSize.x);

                            var center = entityNode.getChildTransformNodes(true, n => n.id === "center")[0];

                            createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, MathHelper.getCircleXYZ(0,radius, center.position), MathHelper.getRotationVector(Math.PI / 4, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, 1));
                            createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, MathHelper.getCircleXYZ((Math.PI*2)*0.25, radius, center.position), MathHelper.getRotationVector(Math.PI / 4, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, 1));
                            createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, MathHelper.getCircleXYZ(Math.PI, radius,center.position), MathHelper.getRotationVector(Math.PI / 4, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode,  MathHelper.getCircleXYZ((Math.PI*2)*0.75,radius, center.position), MathHelper.getRotationVector(Math.PI / 4, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));


                         } else {

                            createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);                           
                           /* if (product.width >= product.depth && product.width <= seriesDepth) {
                                createFreestandingAttachments(entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                            } else {
                                createAttachmentPoint("left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint("right_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, 1));
                                createAttachmentPoint("right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint("left_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, -1));
                            }*/
                        }
                    }
                        break;
                    case "CT02": {
                        createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, seriesDepth - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                    }
                        break;
                    case "CT03": {
                        createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, seriesDepth - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                    }
                        break;
                    case "CT04": {
                        createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, seriesDepth - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                        createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                        createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, seriesDepth - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                    }
                        break;
                    case "CT10": {
                     
                        if (entityFront && entityRight) {                              
                            createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, seriesDepth - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));                                                       
                        }

                        if (entityFront && entityLeft) {    
                            createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, seriesDepth - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                            createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, seriesDepth - offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, 1));                                     
                        }                            
                                               
                    }
                        break;
                    case "CT11": {
                        if (product.width !== product.depth) {
                            if (entity.connections.map(x => x.id).includes("CT03")) {
                                createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                                createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            } else {                              
                                createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                                createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                                createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                            }
                        } else {
                            if (entity.connections.map(x => x.id).includes("CT03")) {
                                createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, seriesDepth - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, -1));
                            } else {
                                createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, seriesDepth - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, seriesDepth - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));

                            }

                        }
                    }
                        break;
                }
            }
                break;
            case "BACK": {

                  if ([148882,157547,157546].includes(product.series)) {

                    switch (entityConnectionType) {
                        case "CT12":
                        case "CT34":
                        case "CT35":    
                        case "CT36":
                            createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                            createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                            break;
                        case "CT37":
                        case "CT38":
                        case "CT39":   
                        case "CT40":    
                            createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                            createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ + 0.2), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                            createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ + 0.2), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                        break;
                        default: 
                            createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                            break;
                    }
                }
            }
                break;
            case "ARM":
            case "ARM_LEFT":
            case "ARM_RIGHT": {

                if ([148882,157547,157546].includes(product.series)) {
              
                    switch (entityConnectionType) {
                        case "CT45":
                        case "CT48":
                            createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                            createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));                             
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));

                            if (product.depth >= 120) {
                                createAttachmentPoint(ctx, "right_center", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ/2 - offsetZ - offsetFrontConnection), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            }
                            break;
                        case "CT44":
                        case "CT47":
                            createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                            createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));

                            if (product.depth >= 120) {
                                createAttachmentPoint(ctx, "left_center", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ/2 - offsetZ - offsetFrontConnection), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            }
                            break;
                        case "CT46": 
                        case "CT49": 
                            createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                            createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));

                             if (product.depth >= 120) {                                 
                                createAttachmentPoint(ctx, "left_center", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ/2 - offsetZ - offsetFrontConnection), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                                createAttachmentPoint(ctx, "right_center", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ/2 - offsetZ - offsetFrontConnection), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            }
                            break;
                        default: 
                            createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                            break;
                    }
                } else {

                      switch (entityConnectionType) {

                        case "CT02": {                                
                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));    
                        }
                            break;
                        case "CT03": {
                            if (product.width >= 40) { 
                                createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));  
                            }                             
                        }
                            break;
                        case "CT04": {
                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));

                            if (product.width >= 40) { 
                                createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                            }
                        }
                            break;
                        default: 
                            createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                            break;
                    }
                }
            }
                break;               
            case "SEATFRAME": {                  
              
                //Bure
                if ([148882,157547,157546].includes(product.series)) { 
                    
                    //Single legs 
                    createAttachmentPoint(ctx, "left_front_single", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ - offsetFrontSingle*2), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), BABYLON.Vector3.One());
                    createAttachmentPoint(ctx, "right_front_single", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ - offsetFrontSingle*2), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));                              
                                                    
                    if (!entity.connections.map(x => x.id).includes("CT11")) {
                        createAttachmentPoint(ctx, "left_back_single", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ + offsetFrontSingle*2), MathHelper.getRotationVector(-Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(1, 1, -1));
                        createAttachmentPoint(ctx, "right_back_single", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ + offsetFrontSingle*2), MathHelper.getRotationVector(Math.PI / 2, 0, BABYLON.Axis.Y), new BABYLON.Vector3(-1, 1, -1));
                    }

                    let frontOffsetZ = positionZ
                    if (leftDepth > 0 && product.depth > leftDepth) {        
                        if (entityLeft.entity.category === "SEATFRAME")                 
                            frontOffsetZ = leftOffsetZ  
                    }
                    if (rightDepth > 0 && product.depth > rightDepth) {  
                        if (entityRight.entity.category === "SEATFRAME")                              
                            frontOffsetZ = rightOffsetZ   
                    }  

                    switch (entityConnectionType) {
                        case "CT02": {

                            if ((leftDepth > 0 && product.depth < leftDepth) || adjacentEntities.filter(x => x.direction === "LEFT").map(x => x.entity.category).includes("BACK")) {    
                                createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            }

                            if ((rightDepth > 0 && product.depth <= rightDepth) || adjacentEntities.filter(x => x.direction === "RIGHT").map(x => x.entity.category).includes("BACK")) {
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", `leg lt02`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));                                                        
                            }                     
                           
                        }
                        break;
                        case "CT03": {
                            if ((leftDepth > 0 && product.depth < leftDepth) || adjacentEntities.filter(x => x.direction === "LEFT").map(x => x.entity.category).includes("BACK")) {                                 
                                createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));                                   
                            }
                        }
                        break;
                        case "CT44": {
                           
                            if (adjacentEntities.filter(x => x.direction === "LEFT").map(x => x.entity.category).includes("BACK")) {              
                                createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            }                              

                            if ((rightDepth > 0 && product.depth <= rightDepth)) {                                  
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", `leg lt02`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));                                  
                            }
                       
                        }
                            break;
                        case "CT04": 
                        {
                             if ((rightDepth > 0 && product.depth <= rightDepth) || adjacentEntities.filter(x => x.direction === "RIGHT").map(x => x.entity.category).includes("BACK")) {                                 
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", `leg lt02`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));                                  
                             }
                        }
                        break;
                        case "CT45": {

                          
                           if (adjacentEntities.filter(x => x.direction === "RIGHT").map(x => x.entity.category).includes("BACK")) {                      
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", `leg lt02`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));  
                           }   
                           
                           if ((leftDepth > 0 && product.depth < leftDepth)) {                                  
                                createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));                                 
                            }
                           
                        }
                            break;
                        case "CT11":  
                        case "CT25":
                        case "CT26":     
                        case "CT36":                          
                        case "CT41":     
                                 
                            if (adjacentEntities.filter(x => x.direction === "LEFT").map(x => x.entity.category).includes("BACK") ||
                                (leftDepth > 0 && product.depth < leftDepth) && entity.connections.map(x => x.id).includes("CT03")) {                                                
                                createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            }                              

                            if ( adjacentEntities.filter(x => x.direction === "RIGHT").map(x => x.entity.category).includes("BACK") ||
                               (rightDepth > 0 && product.depth <= rightDepth) && entity.connections.map(x => x.id).includes("CT04")) {  
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", `leg lt02`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1)); 
                            } 

                            break;                          
                        case "CT46":   

                            if (adjacentEntities.filter(x => x.direction === "LEFT").map(x => x.entity.category).includes("BACK") || 
                                (leftDepth > 0 && product.depth < leftDepth)) {       
                                createAttachmentPoint(ctx, "left_front", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                                createAttachmentPoint(ctx, "left_back", "leg lt02", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1)); 
                            } 

                            if (adjacentEntities.filter(x => x.direction === "RIGHT").map(x => x.entity.category).includes("BACK") || 
                                (rightDepth > 0 && product.depth <= rightDepth)) {                          
                                createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                                createAttachmentPoint(ctx, "right_front", `leg lt02`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, 1));   
                            }

                            break;
                        default: 
                            createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                            break;
                    }

                 } else {
             
                    //Default (Chaiselong etc.)
                    switch (entityConnectionType) {
                        case "CT02": {

                            let legFrontType = "lt02";

                            if (product.depth > rightDepth) {                                                    
                               createAttachmentPoint(ctx, "right_center", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, rightOffsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                               legFrontType = "lt01"
                            }

                            if (product.depth > leftDepth) {
                                createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                            }

                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", `leg ${legFrontType}`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                          

                        }
                            break;
                        case "CT03": {  

                            if (product.depth > leftDepth) {
                                createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());
                            }

                            createAttachmentPoint(ctx, "right_back", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", "leg lt01", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));  


                        }
                            break;
                        case "CT04": {

                            let legFrontType = "lt02";

                            if (product.depth > rightDepth) {                                                    
                               createAttachmentPoint(ctx, "right_center", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, rightOffsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                               legFrontType = "lt01"
                            }

                            createAttachmentPoint(ctx, "right_back", "leg lt02", entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, -1));
                            createAttachmentPoint(ctx, "right_front", `leg ${legFrontType}`, entityNode, new BABYLON.Vector3(-positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(-1, 1, 1));
                            createAttachmentPoint(ctx, "left_back", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, offsetZ), BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 1, -1));
                            createAttachmentPoint(ctx, "left_front", "leg lt01", entityNode, new BABYLON.Vector3(positionX - offsetX, 0, positionZ - offsetZ), BABYLON.Vector3.Zero(), BABYLON.Vector3.One());

                        }
                            break;
                        default: 
                            createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                            break;
                    }

                 }
            }
                break;
            case "CHAIR": 
            case "CHAIR_WO_ARM":
            case "CHAIR_LEFT": 
            case "CHAIR_RIGHT": {
                createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
            }
                break;
            default: 
                createFreestandingAttachments(ctx, entity, entityNode, positionX, positionZ, offsetX, offsetZ);
                break;
        }
    }
}

export async function modifyLegAttachments(ctx: AppContext, module: Module, entity: Entity, product: Product, scene: BABYLON.Scene) {

    var seriesDepth = ConversionHelper.scale(getSeriesDepth(ctx), ctx.scaleFactor) as number;
    var series = await seriesLibrary.fetchSeriesByID(product.series);   
    var entityNode = scene.getTransformNodeByUniqueId(entity.uniqueId);
    
    if (entityNode) {

        var entityBounds = entityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
        entityBounds.refreshBoundingInfo()
        var bv = entityBounds.getHierarchyBoundingVectors(true);
        var bb = new BABYLON.BoundingBox(bv.min, bv.max);

        var offsetX = 0;
        var offsetZ = 0;

        let rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(-entity.rotation._y, entity.rotation._x, entity.rotation._z);            
        let entityExtendSize = BABYLON.Vector3.TransformCoordinates(bb.extendSize, rotationMatrix);    

        var positionX = Math.abs(entityExtendSize.x);
        var positionZ = Math.abs(entityExtendSize.z) * 2;

        var leftFrontAttachment = scene.getTransformNodeById(`${entityNode.id}_attachment_left_front`)   
        var rightFrontAttachment = scene.getTransformNodeById(`${entityNode.id}_attachment_right_front`)  
        var leftBackAttachment = scene.getTransformNodeById(`${entityNode.id}_attachment_left_back`)   
        var rightBackAttachment = scene.getTransformNodeById(`${entityNode.id}_attachment_right_back`)
     
        switch (entity.category) {
            case "FOOTSTOOL": {
            if ([1908,6051].includes(series.id)) {  

                if (entityExtendSize.x < entityExtendSize.z) {

                        rightFrontAttachment.rotation = new BABYLON.Vector3(0,Math.PI/2,0);
                        rightFrontAttachment.scaling = new BABYLON.Vector3(-1,1,-1);

                        rightBackAttachment.rotation = new BABYLON.Vector3(0,Math.PI/2,0);
                        rightBackAttachment.scaling = new BABYLON.Vector3(1,1,-1);

                        leftFrontAttachment.rotation = new BABYLON.Vector3(0,Math.PI/2,0);
                        leftFrontAttachment.scaling = new BABYLON.Vector3(-1,1,1);

                        leftBackAttachment.rotation = new BABYLON.Vector3(0,Math.PI/2,0);
                        leftBackAttachment.scaling = new BABYLON.Vector3(1,1,1);
                    }                 
                }
            }

        }
    }
}

export async function constructLegs(ctx: AppContext, module: Module, entity: Entity) {

    //Filter out existing legs
    module.entities = module.entities.filter(x => x.parentId !== entity.id);

    const entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
    const entityProduct = productLibrary.getProductByProductNo(entity.productNo);
    const series = await seriesLibrary.fetchSeriesByID(entityProduct.series);

    const attachmentNodes = entityNode.getChildTransformNodes(true, function (cm) { return BABYLON.Tags.MatchesQuery(cm, "leg") });
    attachmentNodes.forEach(function (attachmentNode) {

        const legTags = BABYLON.Tags.GetTags(attachmentNode) as string;
        const legType: string = legTags.split(" ")[1];
        const legProduct = seriesLibrary.getSeriesLeg(series, "standard", legType);
        if (!legProduct)
            return;

        // The configuration's lift (offsetY) comes from the first leg found
        if (!ctx.currentConfiguration.data.offsetY) {
            ctx.currentConfiguration.data.offsetY = ConversionHelper.scale(legProduct.height, 0.001) as number || 0;
        }

        if (LegHelper.isBuiltInLeg(legType)) {
            ctx.currentConfiguration.data.offsetY = 0;
            return;
        }

        LegHelper.addLegToAttachment(ctx, module, entity, attachmentNode, legProduct, legType);
    });
}
